import { Appointment, AppointmentStatuses } from '@chiroup/core/types/Appointment.type';
import { useState } from 'react';
import { QueryFunctionContext, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import locationService from '../services/location.service';

const getQuery = () => {
  return async (context: QueryFunctionContext) => {
    const path = context.queryKey[1] as string;
    if (!path) return {};

    return locationService.getAppointments(path);
  };
};

const useAppointments = () => {
  const { path } = useParams();
  const queryClient = useQueryClient();
  const [isCancelling, setIsCancelling] = useState(false);

  const { status, data, error, isFetching, refetch } = useQuery<Appointment[]>(
    ['appointments', path],
    getQuery(),
    {
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const cancel = async (appointmentId: string, reason: string) => {
    setIsCancelling(true);

    try {
      await locationService.cancelAppointment(appointmentId, reason);
    } catch (e) {
      setIsCancelling(false);
      throw e;
    }

    queryClient.setQueryData(['appointments', path], (prev?: Appointment[]) => {
      if (!prev) return [];
      const canceledAppt = prev.find(item => item.id === appointmentId);

      if (!canceledAppt) {
        return prev;
      }

      const updatedData = prev.map(item => {
        if (item.groupId === canceledAppt.groupId) {
          return {
            ...item,
            status: AppointmentStatuses.Canceled,
          };
        }

        return item;
      });

      return updatedData;
    });
    setIsCancelling(false);
  };

  const updateAppointment = (id: string, data: Partial<Appointment>) => {
    queryClient.setQueryData(['appointments', path], (prev?: Appointment[]) => {
      return (
        prev?.map((appt: any) => {
          if (appt.id === id) {
            return {
              ...appt,
              ...data,
            };
          }

          return appt;
        }) || []
      );
    });
  };

  return {
    status,
    data,
    error,
    isFetching,
    refetch,
    cancel,
    isCancelling,
    updateAppointment,
  };
};

export default useAppointments;
